import {
  chain, isEmpty, forEach, findIndex, head, last, keys, pickBy,
} from 'lodash';

import { captureException } from '@playbookux/playbook-web-core';
import {
  SURVEY_PROJECT_VALUES,
  SURVEY_RESET_QUESTIONS,
  SURVEY_TEST_SESSION,
  SURVEY_QUESTIONS,
  SURVEY_PAGE_QUESTIONS_ANSWERS,
  NEXT_SURVEY_PAGE_QUESTIONS,
  SURVEY_ERROR,
} from './action-types';

import { TYPE_QUESTIONS } from '../components/questions-components';
import { orderArray } from '../utils/order-array';
import { saveTestSessionAnswers } from '../graphql/survey';
import { buildSkipLogic, buildDisplayLogic } from '../../tester/pages/survey/helpers';

const END_SCREEN = 'end_screen';

const findPageIndex = (surveyPages, id) => findIndex(surveyPages, (surveyPage) => (
  surveyPage.id === id
));

export const setSurveyProjectValues = (project) => async (dispatch) => {
  dispatch({
    type: SURVEY_PROJECT_VALUES,
    payload: { project },
  });
};

export const clearSurveyQuestions = () => async (dispatch) => {
  dispatch({
    type: SURVEY_RESET_QUESTIONS,
  });
};

export const setSurveyError = (error) => (dispatch) => {
  dispatch({
    type: SURVEY_ERROR,
    payload: { error },
  });
};

export const setSurveyTestSession = (testSession, isPreview = false) => async (dispatch) => {
  dispatch({
    type: SURVEY_TEST_SESSION,
    payload: { testSession, isPreview },
  });
};

export const setSurveyPageQuestions = (surveysPages, randomize) => async (dispatch) => {
  const allQuestions = [];
  let orderedQuestions = [];

  const surveyPagesOrdered = chain(surveysPages)
    .sortBy('order')
    .value();

  const surveyPages = surveyPagesOrdered.map((surveyPageOrdered) => {
    const { questions } = surveyPageOrdered;
    orderedQuestions = [];

    if (!isEmpty(questions)) {
      orderedQuestions = orderArray(questions, 'questionOrder', randomize);

      forEach(orderedQuestions, (orderedQuestion) => {
        allQuestions.push({
          ...orderedQuestion,
        });
      });
    }
    return {
      ...surveyPageOrdered,
      questions: orderedQuestions,
    };
  });

  dispatch({
    type: SURVEY_QUESTIONS,
    payload: {
      currentSurveyPage: head(surveyPages),
      questions: allQuestions,
      surveyPages,
      maxSurveyPages: surveyPages.length,
    },
  });
};

const getAllAnswers = (surveyPagesAnswers) => surveyPagesAnswers.reduce((acc, pageAnswers) => ([...acc, ...pageAnswers]), []);

/**
 * This function is called when the user submits the answers for a survey page.
 * @param {*} nextPageId
 * @returns Promise<{ currentSurveyPage, isLastSurveyPage }>
 */
export const setNextSurveyPage = (nextPageId = null) => async (dispatch, getState) => {
  const isEndScreen = nextPageId === END_SCREEN;
  const { survey } = getState();
  const { surveyPages, surveyPagesAnswers } = survey;
  const allAnswers = getAllAnswers(surveyPagesAnswers);

  let { currentSurveyPage } = survey;
  let foundPageWithQuestions = false;
  let pageIndex;

  if (isEndScreen) {
    const { id: lastPageId } = last(surveyPages);
    pageIndex = findPageIndex(surveyPages, lastPageId);
  } else if (nextPageId) {
    pageIndex = findPageIndex(surveyPages, nextPageId) - 1;
  } else {
    pageIndex = findPageIndex(surveyPages, currentSurveyPage.id);
  }

  if (pageIndex + 1 < surveyPages.length) {
    currentSurveyPage = surveyPages[pageIndex += 1];
  }

  let isLastSurveyPage = true;

  while (pageIndex <= surveyPages.length) {
    const questions = currentSurveyPage?.questions || [];
    const questionVisibility = buildDisplayLogic(allAnswers, surveyPages);

    const visibleQuestions = questions.filter((q) => {
      const isVisible = questionVisibility[q.id] !== undefined ? questionVisibility[q.id] : true;
      return isVisible;
    });

    if (visibleQuestions.length > 0) {
      foundPageWithQuestions = true;
      // Verify if there is a next page with visible questions
      const nextPossiblePage = surveyPages.slice(pageIndex + 1).find((page) => {
        const q = page.questions || [];
        return q.some((question) => questionVisibility[question.id] !== false);
      });

      if (nextPossiblePage) {
        isLastSurveyPage = false;
      }

      break; // Found a page with visible questions
    } else {
      // Skip to the next page
      currentSurveyPage = surveyPages[pageIndex += 1];
    }
  }

  if (!foundPageWithQuestions) {
    // There are no more pages with questions
    isLastSurveyPage = true;
  }

  if (currentSurveyPage === undefined) {
    currentSurveyPage = {};
  }

  dispatch({
    type: NEXT_SURVEY_PAGE_QUESTIONS,
    payload: {
      currentSurveyPage,
      isLastSurveyPage,
    },
  });

  return {
    currentSurveyPage,
    isLastSurveyPage,
  };
};

/**
 * This function is called when the user submits the answers for a survey page.
 *
 * @param {*} question
 * @param {*} state
 * @returns answer
 */
export const setSurveyPageAnswerValue = (question, state) => {
  const { testSession } = state;

  const answer = {
    questionId: question.id,
    testSessionId: testSession.id,
    valueText: '',
    videoEndTime: 0,
    videoStartTime: 0,
    ranking: [],
    answersMatrix: JSON.stringify({}),
  };

  switch (question.questionType) {
    case TYPE_QUESTIONS.WRITTEN: {
      answer.valueText = question.answer;
      break;
    }
    case TYPE_QUESTIONS.RANGE: {
      answer.questionOptionId = question.answer;
      break;
    }
    case TYPE_QUESTIONS.SINGLE_SELECT: {
      const { optionDescription } = question.answer;
      answer.questionOptionId = head(keys(question.answer));
      answer.valueText = optionDescription;
      break;
    }
    case TYPE_QUESTIONS.MULTIPLE_SELECT: {
      const { optionDescription } = question.answer;
      answer.questionOptionIds = keys(pickBy(question.answer, (value, key) => key !== 'optionDescription'));
      answer.valueText = optionDescription;
      break;
    }
    case TYPE_QUESTIONS.TEXT_QUESTION: {
      answer.valueText = '';
      break;
    }
    case TYPE_QUESTIONS.PREFERENCE: {
      const singleAnswered = head(keys(question.answer));
      answer.questionOptionId = singleAnswered;
      break;
    }
    case TYPE_QUESTIONS.FIRST_CLICK_TEST: {
      const { x, y } = question.answer;
      answer.firstClickCoordinates = `${x},${y}`;
      break;
    }
    case TYPE_QUESTIONS.RANKING: {
      answer.ranking.push(...question.answer);
      break;
    }
    case TYPE_QUESTIONS.MATRIX: {
      answer.answersMatrix = JSON.stringify(question.answer);
      break;
    }
    default: break;
  }

  return answer;
};

/**
 * This function is called when the user submits the answers for a survey page.
 *
 * @param {*} newAnswers
 * @param {*} isCompleted
 * @param {*} isPreview
 * @returns Promise<boolean>
 */
export const saveAnswers = (newAnswers, isCompleted, isPreview = false) => async (dispatch, getState) => {
  let sessionCompleted = isCompleted;

  const { survey } = getState();
  const { surveyPagesAnswers: previousAnswers } = survey;

  try {
    const attributes = [...previousAnswers, newAnswers].flat(1);
    if (!isPreview) {
      await saveTestSessionAnswers({ attributes });
    }

    sessionCompleted = true;
    await dispatch(clearSurveyQuestions());
  } catch (err) {
    captureException('SET_ANSWERS_FAILED', err);
    dispatch(setSurveyError({ message: err.message }));
    sessionCompleted = false;
  }

  return sessionCompleted;
};

/**
 * This function is called when the user submits the answers for a survey page.
 *
 * @param {*} questions
 * @param {*} isPreviewStudy
 * @returns Promise<{ sessionCompleted: boolean }>
 */
export const setSurveyPageAnswers = (questions, isPreviewStudy, isLastSurveyPage) => async (dispatch, getState) => {
  try {
    const { survey } = getState();
    const { currentSurveyPage } = survey;

    let sessionCompleted = false;
    const answers = [];

    forEach(questions, (question) => {
      const questionAnswers = setSurveyPageAnswerValue(question, survey);
      answers.push(questionAnswers);
    });

    const nextPageId = buildSkipLogic(answers, currentSurveyPage);
    const isEndScreen = nextPageId === END_SCREEN;
    if (isPreviewStudy && isLastSurveyPage) {
      dispatch(clearSurveyQuestions());
      return { sessionCompleted: true };
    }

    if (!isLastSurveyPage) {
      dispatch({
        type: SURVEY_PAGE_QUESTIONS_ANSWERS,
        payload: { answers },
      });
      // when skip to end screen we should save the answers and finish study
      if (isEndScreen) {
        sessionCompleted = await dispatch(saveAnswers(answers, sessionCompleted, isPreviewStudy));
        return { sessionCompleted };
      }

      const nextSurveyPage = await dispatch(setNextSurveyPage(nextPageId));
      const {
        currentSurveyPage: currentSurveyPageNext,
        isLastSurveyPage: isLastSurveyPageNext,
      } = nextSurveyPage;

      if (isLastSurveyPageNext && isEmpty(currentSurveyPageNext) && !isEndScreen) {
        sessionCompleted = await dispatch(saveAnswers(answers, sessionCompleted, isPreviewStudy));
      } else {
        sessionCompleted = false;
      }
    } else {
      sessionCompleted = await dispatch(saveAnswers(answers, sessionCompleted, isPreviewStudy));
    }
    return { sessionCompleted };
  } catch (error) {
    captureException('SET_SURVEY_PAGE_ANSWER_FAILED', error);
    dispatch(setSurveyError({ message: error.message }));
    return { sessionCompleted: false };
  }
};
